<script setup>
import { ref, computed, onMounted } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import SiteAdminService from '@/services/SiteAdminService'
import { useSiteAdmin } from '@/composables/useSiteAdmin'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconChevronUp from '@/components/icon/IconChevronUp.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'

// Categories group content into rails. They carry no access level of their
// own (access model v2): who can watch is set per content item.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const admin = useSiteAdmin(() => props.siteId)

const loading = ref(true)
const modalOpen = ref(false)
const saving = ref(false)
const error = ref(null)
const editing = ref(null)
const form = ref({ name: '', published: true })
const pendingDelete = ref(null)
const reordering = ref(false)

const categories = computed(() => admin.categories.value)
const canSubmit = computed(() => form.value.name.trim().length > 0 && !saving.value)

onMounted(async () => {
	loading.value = true
	await admin.loadCategories()
	loading.value = false
})

const openCreate = () => {
	editing.value = null
	form.value = { name: '', published: true }
	error.value = null
	modalOpen.value = true
}
const openEdit = (cat) => {
	editing.value = cat
	form.value = { name: cat.name, published: cat.published !== false }
	error.value = null
	modalOpen.value = true
}

const save = async () => {
	if (!canSubmit.value) return
	saving.value = true
	error.value = null
	const payload = { name: form.value.name.trim(), published: form.value.published }
	try {
		if (editing.value) await SiteAdminService.updateCategory(props.siteId, editing.value.id, payload)
		else await SiteAdminService.createCategory(props.siteId, payload)
		await admin.loadCategories()
		notify({ group: 'success', text: editing.value ? 'Category updated.' : 'Category created.' })
		modalOpen.value = false
	} catch (err) {
		error.value = err.message || 'Could not save the category.'
	}
	saving.value = false
}

const requestDelete = (cat) => {
	pendingDelete.value = cat
	showModal('delete-site-category')
}
const confirmDelete = async () => {
	if (!pendingDelete.value) return
	try {
		await SiteAdminService.deleteCategory(props.siteId, pendingDelete.value.id)
		notify({ group: 'success', text: `"${pendingDelete.value.name}" deleted.` })
		await admin.loadCategories()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not delete the category.' })
	}
	pendingDelete.value = null
}

const move = async (index, delta) => {
	const target = index + delta
	if (target < 0 || target >= categories.value.length || reordering.value) return
	const ids = categories.value.map((c) => c.id)
	const [moved] = ids.splice(index, 1)
	ids.splice(target, 0, moved)
	reordering.value = true
	try {
		const res = await SiteAdminService.reorderCategories(props.siteId, ids)
		if (res?.items) admin.categories.value = res.items
		else await admin.loadCategories()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not reorder.' })
	}
	reordering.value = false
}
</script>

<template>
	<div>
		<div class="mb-4 flex flex-wrap items-center gap-3">
			<div class="min-w-0 flex-1 basis-72">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Categories</h2>
				<p class="mb-0 text-tiny text-surface-7">Group content into rails on the home page. Who can watch is set on each content item.</p>
			</div>
			<FormButton size="md" class="shrink-0" @click="openCreate">
				<icon-base class="mr-1.5" :width="14" :height="14"><icon-plus /></icon-base>
				New category
			</FormButton>
		</div>

		<Alert v-if="admin.errors.value.categories" type="error" class="mb-4">{{ admin.errors.value.categories.message }}</Alert>

		<Spinner v-if="loading" text="Loading categories…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<EmptyState v-else-if="!categories.length" title="No categories yet" subtitle="Categories become rails on the home page; add items to them from the Library.">
			<template #actions>
				<FormButton class="w-64 justify-center" @click="openCreate">New category</FormButton>
			</template>
		</EmptyState>

		<ul v-else class="m-0 flex list-none flex-col gap-2 p-0">
			<li v-for="(cat, index) in categories" :key="cat.id" class="rounded-lg bg-surface-2" :data-testid="`category-row-${cat.id}`">
				<div class="flex items-center gap-3 px-3.5 py-3">
					<div class="flex shrink-0 flex-col">
						<button type="button" class="text-surface-7 hover:text-white disabled:opacity-30" :disabled="index === 0 || reordering" title="Move up" @click="move(index, -1)">
							<icon-base :width="14" :height="14"><icon-chevron-up /></icon-base>
						</button>
						<button type="button" class="text-surface-7 hover:text-white disabled:opacity-30" :disabled="index === categories.length - 1 || reordering" title="Move down" @click="move(index, 1)">
							<icon-base :width="14" :height="14"><icon-chevron-down /></icon-base>
						</button>
					</div>
					<div class="min-w-0 flex-1">
						<div class="flex flex-wrap items-center gap-2">
							<h3 class="mb-0 truncate text-sm font-medium text-white">{{ cat.name }}</h3>
							<StatusPill v-if="cat.published === false" pill size="sm" variant="muted">Hidden</StatusPill>
						</div>
						<p class="mb-0 text-tiny text-surface-7">
							{{ cat.contentCount ?? 0 }} {{ (cat.contentCount ?? 0) === 1 ? 'item' : 'items' }}
						</p>
					</div>
					<div class="flex shrink-0 items-center gap-1">
						<FormButton is-icon type="secondary" size="sm" title="Edit" @click="openEdit(cat)">
							<icon-base :width="16" :height="16" fill="none"><icon-edit /></icon-base>
						</FormButton>
						<FormButton is-icon type="danger" size="sm" title="Delete" @click="requestDelete(cat)">
							<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
						</FormButton>
					</div>
				</div>
			</li>
		</ul>

		<ModalBox v-model="modalOpen" :title="editing ? 'Edit category' : 'New category'" size="sm" scroll-body wrapper-classes="lg:h-auto lg:max-h-[85vh]">
			<form class="flex flex-col gap-4 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="category-form" @submit.prevent="save">
				<Alert v-if="error" type="error" class="!mb-0">{{ error }}</Alert>
				<FormInput id="category-name" v-model="form.name" label="Name" placeholder="e.g. Live events" input-classes="placeholder:text-surface-6" :maxlength="255" />
				<FormSwitch id="category-published" v-model="form.published" :label="form.published ? 'Visible on the site' : 'Hidden'" />
				<p class="mb-0 text-tiny text-surface-7">Who can watch (Public, Login required or Paid) is set on each item in the Library.</p>
			</form>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" :disabled="!canSubmit" :is-loading="saving" @click="save">
						{{ editing ? 'Save' : 'Create category' }}
					</FormButton>
					<FormButton class="w-full justify-center sm:w-auto" type="secondary" @click="modalOpen = false">Cancel</FormButton>
				</footer>
			</template>
		</ModalBox>

		<ConfirmModal modal-id="delete-site-category" modal-type="danger" ok-text="Delete" cancel-text="Cancel"
			title="Delete this category?" :message="`&quot;${pendingDelete?.name || ''}&quot; is removed; its items stay on the site without this category.`"
			@modal-confirm="confirmDelete" />
	</div>
</template>
